<script lang="ts" setup>
import { Dayjs } from 'dayjs'
import _ from 'lodash'
import { computed, ComputedRef, onMounted, Ref, ref, watch } from 'vue'

import AutomationPage from '@/automation/AutomationPage.vue'
import ChartGraph from '@/home/ChartGraph.vue'
import ChartSelector from '@/home/ChartSelector.vue'
import HomeList from '@/home/HomeList.vue'
import HomeListSelector from '@/home/HomeListSelector.vue'
import { HomeChartStatsPayload, HomeStatsPayload } from '@/home/types'
import useDialysisMedicalUnitsSelection from '@/misc/composables/useDialysisMedicalUnitsSelection'
import { hasPermission, RoleEnum } from '@/misc/permissions/permissionsHelper'
import { formatQuery } from '@/misc/string_utils'
import router from '@/router'
import { SavedSearchesPayload } from '@/saved-searches/types'
import StatsSettings from '@/stats/StatsSettings.vue'
import { useStore } from '@/store'
import { CountryEnum, ModeEnum, PmsiTypeEnum, PrivacyEnum, SearchGroupPrivacyEnum } from '@/store/modules/settings/types'
import { HospitalizationType } from '@/store/modules/stay/smr/types'

type ChartName = 'waitingStays' | 'shortStays' | 'revaluableStays' | 'automatableStays'
type ChartMode = 'global' | ModeEnum

const store = useStore()

const props = withDefaults(defineProps<{
  pmsiType?: PmsiTypeEnum
}>(), {
  pmsiType: PmsiTypeEnum.MCO
})

const chartName: Ref<ChartName> = ref('waitingStays')
const chartMode: Ref<ChartMode> = ref('global')
const chartInterval = ref(1)
const routeQuery = computed(() => store.state.route.query)
const chartUnit: Ref<string> = ref(routeQuery.value.cu ? routeQuery.value.cu : 'month')

const mode = computed(() => store.state.settings.pmsi.mode)
const pmsiCountry = computed(() => store.state.settings.pmsi.country)
const upToDateModes: Ref<ModeEnum[]> = ref([])

const isAutomationEnabled = computed(() =>
  store.state.settings.sancareSettings.boolSettings ? (
    store.state.settings.sancareSettings.boolSettings.session_automation.value === true
    || store.state.settings.sancareSettings.boolSettings.non_session_automation.value === true
  ) : false
)

// à la toute première visite le localStorage est vide : on ne sait rien tant que l'API n'a pas répondu
const areSancareSettingsLoaded = computed(() => store.state.settings.sancareSettings.boolSettings !== undefined)

const isDialysisAllowed = computed(() =>
  props.pmsiType === PmsiTypeEnum.MCO
  && pmsiCountry.value !== CountryEnum.CH
  && store.getters.flatSancareSettings['enable_dialyse'] === true
)
const isDialysisMode = computed(() => mode.value === ModeEnum.DIALYSIS && isDialysisAllowed.value)
const dialysisMedicalUnitIds: ComputedRef<number[]> = computed(() => store.getters.dialysisMedicalUnitIds)
const { restrictSelectedMedicalUnitsToDialysis } = useDialysisMedicalUnitsSelection(isDialysisMode)

const statsInfo = computed(() => {
  let color: string
  switch (chartName.value) {
    case 'waitingStays':
      color = 'orange'
      break
    case 'shortStays':
      color = 'green'
      break
    case 'revaluableStays':
      color = 'yellow'
      break
  }
  return color
    ? {
      countValidatedStay: { label: 'Validés', color },
      countTodoStay: { label: 'A traiter', color: 'grey' },
      countTotalStay: { label: 'Total', hidden: true },
    }
    : null
})

const earlyLimit: Ref<Dayjs> = computed(() => store.state.settings.earlyLimit)
const lateLimit: Ref<Dayjs> = computed(() => store.state.settings.lateLimit)
const teamMode = computed(() => store.state.login.currentUser.teamMode)
const selectedMedicalUnits: Ref<number[] | null> = computed(() => store.state.settings.selectedMedicalUnits)
const canManageAutomatisation = computed(() => hasPermission(RoleEnum.ROLE_AUTOMATION_MANAGER, store.state.login.userRole))
const globalStats = computed(() => store.state.home.homeStats)
const isGlobalStatsLoading = computed(() => Boolean(store.state.home.requests.getHomeStats.fetching))
const hospitalizationTypes = computed(() => store.state.settings.hospitalizationTypes)

const scopedSearches = computed(() => store.state.savedSearches.savedSearches.filter((s) => s.type === mode.value && s.pmsiType === props.pmsiType))
const privateSearches = computed(() => {
  const privateSearches = scopedSearches.value.filter((s) => {
    return s.user.username === store.state.login.username && s.privacy === PrivacyEnum.PRIVATE_SEARCH
  })

  return _.orderBy(privateSearches, ['countTodoStay', 'name'], ['desc', 'asc'])
})

const teamSearches = computed(() => {
  const teamSearches = []
  if (store.state.login.currentUser.team === null || !teamMode.value) {
    return teamSearches
  }

  let modified = scopedSearches.value.filter((s) => {
    return s.privacy === PrivacyEnum.TEAM_SEARCH
      && s.validated === false
      && s.user.team.id === store.state.login.currentUser.team.id
  })

  modified = _.orderBy(modified, ['countTodoStay', 'name'], ['desc', 'asc'])

  teamSearches.push({
    title: 'Recherches en attente de validation',
    searches: modified
  })

  let validated = scopedSearches.value.filter((s) => {
    return s.privacy === PrivacyEnum.TEAM_SEARCH
      && s.validated === true
      && s.user.team.id === store.state.login.currentUser.team.id
  })
  validated = _.orderBy(validated, ['countTodoStay', 'name'], ['desc', 'asc'])

  teamSearches.push(
    {
      title: 'Recherches validées',
      searches: validated
    }
  )
  return teamSearches
})

const publicCategories = computed(() => {
  let categories = []
  for (const search of scopedSearches.value) {
    if (search.privacy !== PrivacyEnum.PUBLIC_SEARCH) {
      continue
    }
    const category = search.savedSearchCategory
    const listCategory = categories.find((listCategory) => listCategory.id === category.id)
    if (listCategory) {
      listCategory.searches.push(search)
      continue
    }
    categories.push({
      ...category,
      searches: [search]
    })
  }
  categories = categories.map((cat) => ({ ...cat, searches: _.orderBy(cat.searches, ['name'], ['asc']) }))
  return _.orderBy(categories, ['name'], ['asc'])
})

const sancareCategories = computed(() => {
  const sancareCategories = _.map(store.state.savedSearches.sancareSavedSearchesCategories, (cat) => {
    const catSearches = scopedSearches.value.filter((s) => {
      return s.savedSearchCategory.id === cat.id && s.privacy === PrivacyEnum.SANCARE_SEARCH
    })

    return {
      id: cat.id,
      name: cat.name,
      countTodoStay: cat.countTodoStay,
      searches: _.orderBy(catSearches, ['countTodoStay', 'name'], ['desc', 'asc'])
    }
  })
  return _.orderBy(
    _.filter(sancareCategories, (cat) => cat.searches && cat.searches.length),
    ['countTodoStay', 'name'],
    ['desc', 'asc']
  )
})

const userSearchGroups = computed(() => {
  if (mode.value !== ModeEnum.OPTIMIZABLE) {
    return []
  }
  return store.state.savedSearches.savedSearchesGroups.filter((group) => group.privacy === SearchGroupPrivacyEnum.PRIVATE)
})

const recentlyUsedSearches = computed(() => store.state.savedSearches.recentlyUsedSearches[mode.value] || [])

const finessLimitList = computed(() => {
  return _.concat(
    store.state.login.currentUser.legalEntities || [],
    store.state.login.currentUser.geographicEntities || []
  )
})

const refreshGlobalStats = () => {
  const payload: HomeStatsPayload = {
    earlyLimit: earlyLimit.value,
    lateLimit: lateLimit.value,
    medicalUnitIds: selectedMedicalUnits.value,
    pmsiType: props.pmsiType,
    hospitalizationTypes: hospitalizationTypes.value,
  }
  store.dispatch('home/getHomeStats', payload)
}

const refreshChartStats = () => {
  const payload: HomeChartStatsPayload = {
    mode: chartMode.value,
    earlyLimit: earlyLimit.value,
    lateLimit: lateLimit.value,
    unit: chartUnit.value,
    medicalUnitIds: selectedMedicalUnits.value,
  }
  store.dispatch('home/getHomeChartStats', payload)
}

function refreshAutomationGroups() {
  if (ModeEnum.AUTOMATION !== mode.value) {
    return
  }
  store.dispatch('getAllAutomationGroups')
  store.dispatch('getAllAutomationGroupsGhmCollisions')
}

const refreshSavedSearches = (searchIds?: number[] | null) => {
  if (mode.value === ModeEnum.AUTOMATION) {
    return
  }
  const payload: SavedSearchesPayload = {
    earlyLimit: earlyLimit.value,
    lateLimit: lateLimit.value,
    medicalUnits: selectedMedicalUnits.value,
    pmsiType: props.pmsiType,
    searchMode: mode.value,
    hospitalizationTypes: hospitalizationTypes.value,
  }
  if (searchIds) {
    payload.searchIds = searchIds
  }
  store.dispatch('fetchSavedSearches', payload)
}

const shouldSancareCategoriesBeFetched = computed(() =>
  (props.pmsiType === PmsiTypeEnum.MCO && [ModeEnum.OPTIMIZABLE, ModeEnum.NEW_CODING, ModeEnum.DIALYSIS].includes(mode.value))
  || (props.pmsiType === PmsiTypeEnum.SMR && [ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS].includes(mode.value))
)

const refreshSancareSearchesCategories = (searchIds?: number[] | null) => {
  if (!shouldSancareCategoriesBeFetched.value) {
    return
  }
  const payload: SavedSearchesPayload = {
    earlyLimit: earlyLimit.value,
    lateLimit: lateLimit.value,
    medicalUnits: selectedMedicalUnits.value,
    pmsiType: props.pmsiType,
    searchMode: mode.value,
    hospitalizationTypes: hospitalizationTypes.value,
  }
  if (searchIds) {
    payload.searchIds = searchIds
  }
  store.dispatch('fetchSancareSavedSearchesCategories', payload)
}

const refreshSavedSearchesGroups = (searchIds?: number[] | null, groupIds?: number[] | null) => {
  if (ModeEnum.OPTIMIZABLE !== mode.value) {
    return
  }
  const payload: SavedSearchesPayload = {
    earlyLimit: earlyLimit.value,
    lateLimit: lateLimit.value,
    medicalUnits: selectedMedicalUnits.value,
    pmsiType: props.pmsiType,
    mode: mode.value,
    hospitalizationTypes: hospitalizationTypes.value,
  }
  if (searchIds) {
    payload.searchIds = searchIds
  }
  if (groupIds) {
    payload.groupIds = groupIds
  }
  store.dispatch('fetchSavedSearchesGroups', payload)
}

const refreshRecentlyUsedSearches = () => {
  if (mode.value === ModeEnum.AUTOMATION) {
    return
  }
  store.dispatch('fetchRecentlyUsedSearches', {
    earlyLimit: earlyLimit.value,
    lateLimit: lateLimit.value,
    medicalUnits: selectedMedicalUnits.value,
    pmsiType: props.pmsiType,
    searchMode: mode.value,
    hospitalizationTypes: hospitalizationTypes.value,
  })
}

type RefreshStatsPayload = { searchIds: number[] | null, groupIds: number[] | null }

const refresh = (refreshStats: boolean, payload?: RefreshStatsPayload) => {
  upToDateModes.value.push(mode.value)
  if (refreshStats && props.pmsiType === PmsiTypeEnum.MCO) {
    refreshChartStats()
  }
  refreshGlobalStats()
  refreshAutomationGroups()
  refreshSavedSearches(payload?.searchIds)
  refreshSancareSearchesCategories(payload?.searchIds)
  refreshSavedSearchesGroups(payload?.searchIds, payload?.groupIds)
  refreshRecentlyUsedSearches()
}

const setDateRange = ({ earlyLimit, lateLimit }: { earlyLimit: Dayjs | null, lateLimit: Dayjs | null }) => {
  store.commit('setEarlyLimit', earlyLimit)
  store.commit('setLateLimit', lateLimit)
  router.replace({
    path: store.state.route.path,
    query: {
      ...store.state.route.query,
      el: earlyLimit?.format('DD-MM-YYYY'),
      ll: lateLimit?.format('DD-MM-YYYY'),
    }
  })
  upToDateModes.value = []
  refresh(true)
}

const setSelectedMedicalUnits = (medicalUnitList: number[]) => {
  store.commit('setSelectedMedicalUnits', medicalUnitList)
  router.replace({
    path: store.state.route.path,
    query: formatQuery({
      ...store.state.route.query,
      mu: medicalUnitList,
    })
  })
  upToDateModes.value = []
  refresh(true)
}

const setHospitalizationTypes = (types: HospitalizationType[]) => {
  store.commit('setHospitalizationTypes', types)
  router.replace({
    path: store.state.route.path,
    query: formatQuery({
      ...store.state.route.query,
      hospitalizationTypes: types,
    })
  })
  upToDateModes.value = []
  refresh(true)
}

const setChartUnit = (unit: string) => {
  chartUnit.value = unit
  refreshChartStats()
  router.replace({
    path: store.state.route.path,
    query: { ...store.state.route.query, cu: unit } })
}

const setChart = (newChartName: ChartName) => {
  chartName.value = newChartName
  switch (chartName.value) {
    case 'waitingStays':
      chartMode.value = 'global'
      break
    case 'automatableStays':
      chartMode.value = ModeEnum.AUTOMATION
      break
    case 'shortStays':
      chartMode.value = ModeEnum.NEW_CODING
      break
    case 'revaluableStays':
      chartMode.value = ModeEnum.OPTIMIZABLE
      break
  }
  refreshChartStats()
}

const getFinessTooltip = (finessLimit) => {
  return `${finessLimit.longDescription && finessLimit.longDescription.length > 0
    ? finessLimit.longDescription
    : finessLimit.shortDescription} (${finessLimit.finess})`
}

async function setList(mode: ModeEnum) {
  await router.replace({
    path: store.state.route.path,
    query: { ...store.state.route.query, mode }
  })
  if (restrictSelectedMedicalUnitsToDialysis()) {
    upToDateModes.value = []
    refresh(true)
  } else if (!upToDateModes.value.includes(mode)) {
    refresh(false)
  }
}

// mode dialyse venu de l'URL : restreindre avant le premier chargement (onMounted)
restrictSelectedMedicalUnitsToDialysis()

watch(dialysisMedicalUnitIds, () => {
  if (restrictSelectedMedicalUnitsToDialysis()) {
    upToDateModes.value = []
    refresh(true)
  }
})

// le mode dialyse peut venir de l'URL (marque-page) alors que le réglage est désactivé
watch([areSancareSettingsLoaded, isDialysisAllowed], ([loaded, allowed]) => {
  if (!loaded || allowed || mode.value !== ModeEnum.DIALYSIS) {
    return
  }
  store.commit('initPmsiMode')
  // setList() et pas juste router.replace() : les données chargées sont celles du mode dialyse
  setList(mode.value)
}, { immediate: true })

watch(teamMode, (_, PreviousTeamMode) => {
  // FIXME: little hack that prevents refresh() to be called twice on homepage initialization
  if (PreviousTeamMode !== null) {
    refresh(true)
  }
})

watch(() => props.pmsiType, () => {
  upToDateModes.value = []
  refresh(true)
})

onMounted(() => {
  refresh(true)
  store.commit('resetStayList')
})
</script>

<template>
  <div
    name="homepage"
    class="container"
  >
    <stats-settings
      :class="{ 'mb-2': pmsiType === PmsiTypeEnum.SMR }"
      :early-limit="earlyLimit"
      :late-limit="lateLimit"
      :chart-unit="chartUnit"
      :are-intervals-displayed="pmsiType === PmsiTypeEnum.MCO"
      :is-dialysis-mode="isDialysisMode"
      @set-date-range="setDateRange"
      @set-chart-unit="setChartUnit"
      @set-selected-medical-units="setSelectedMedicalUnits"
      @set-hospitalization-types="setHospitalizationTypes"
    />
    <chart-selector
      v-if="pmsiType === PmsiTypeEnum.MCO"
      :selected-chart="chartName"
      :chart-interval="chartInterval"
      :stats="globalStats"
      :loading="isGlobalStatsLoading"
      class="row col-12 my-3"
      @set-chart="setChart"
    />
    <chart-graph
      v-if="pmsiType === PmsiTypeEnum.MCO"
      :chart-name="chartName"
      :chart-unit="chartUnit"
      :chart-interval="chartInterval"
      :early-limit="earlyLimit"
      :late-limit="lateLimit"
      :stats-info="statsInfo"
      class="row col-12 mb-2 chart-graph"
    />
    <div
      v-if="finessLimitList && finessLimitList.length"
      class="alert alert-secondary"
    >
      Votre compte est restreint aux établissements suivants:
      <ul class="mb-0">
        <li
          v-for="finessLimit in finessLimitList"
          :key="finessLimit.finess"
        >
          <span
            v-tooltip="getFinessTooltip(finessLimit)"
          >
            {{ finessLimit.shortDescription }}
          </span>
        </li>
      </ul>
    </div>
    <home-list-selector
      :class="{ 'mt-5': pmsiType === PmsiTypeEnum.SMR }"
      :selected-list="mode"
      :is-automation-allowed="canManageAutomatisation && pmsiType === PmsiTypeEnum.MCO"
      :is-automation-enabled="isAutomationEnabled"
      :is-new-coding-allowed="pmsiType === PmsiTypeEnum.MCO"
      :is-optimizable-allowed="pmsiCountry !== CountryEnum.CH"
      :is-dialysis-allowed="isDialysisAllowed"
      @set-list="(mode: ModeEnum) => setList(mode)"
    />
    <div class="home-list mb-2 pt-0">
      <automation-page
        v-if="pmsiType === PmsiTypeEnum.MCO && mode === ModeEnum.AUTOMATION"
        :is-automation-allowed="canManageAutomatisation"
        :is-automation-enabled="isAutomationEnabled"
        :early-limit="earlyLimit"
        :late-limit="lateLimit"
      />
      <home-list
        v-show="mode !== ModeEnum.AUTOMATION"
        :key="mode"
        :mode="mode"
        :pmsi-type="pmsiType"
        :early-limit="earlyLimit"
        :late-limit="lateLimit"
        :private-searches="privateSearches"
        :team-searches="teamSearches"
        :public-categories="publicCategories"
        :user-search-groups="userSearchGroups"
        :sancare-categories="sancareCategories"
        :recently-used-searches="recentlyUsedSearches"
        @refresh-stats="(payload: RefreshStatsPayload) => refresh(true, payload)"
      />
    </div>
  </div>
</template>
